import React from 'react';

// components
import { Link } from 'src/components/Inputs';
import { EmbeddedForeignContentBanner } from 'src/components/Feedback';

// helpers
import { useUserLocale } from 'src/hooks';
import { Locales } from 'src/lib/constants';

const renderForeignContentBanner = (localeCode) => {
  switch (localeCode) {
    case 'en-gb':
      return (
        <EmbeddedForeignContentBanner
          contentLocale={Locales.EN_EU}
          href='#end-user-services-agreement-uk'
        />
      );
    case 'en-us':
      return (
        <EmbeddedForeignContentBanner
          contentLocale={Locales.EN_EU}
          href='#end-user-services-agreement-us'
        />
      );
    default:
      return null;
  }
};

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const EEAEndUserServicesAgreement = () => {
  const { locale: userLocale } = useUserLocale();
  return (
    <>
      <h4
        id='end-user-services-agreement-eea'
        data-magellan-target='end-user-services-agreement-eea'
      >
        End User Services Agreement (EEA)
      </h4>
      {renderForeignContentBanner(userLocale?.localeCode)}
      <p>
        <em>Last Updated: 04 April 2022</em>
      </p>
      <p>Thank you for using Plaid!</p>
      <p>
        This End User Services Agreement (&quot;
        <strong>Agreement</strong>&quot;) is a legal agreement between Plaid, BV
        (&quot;<strong>Plaid</strong>&quot;, &quot;
        <strong>we</strong>&quot; or &quot;<strong>us</strong>&quot;) and the
        end user of our Services (&quot;<strong>you</strong>
        &quot;, &quot;<strong>your</strong>&quot;). This Agreement only applies
        when we provide account information services and payment initiation
        services (within the meaning of the European revised payment service
        directive (EU) 2015/2366) to you with respect to a Netherlands or
        European Economic Area (EEA) payment account, and when we transmit
        retrieved data and/or the payment status related to such services to
        third parties on your behalf (&quot;<strong>Services</strong>
        &quot;). Such third parties include the owner or provider of the
        website, desktop and/or mobile application through which you have
        accessed our Services (&quot;<strong>Application</strong>&quot;).
      </p>
      <p>
        This Agreement describes the terms and conditions that apply to your use
        of the Services. You may not access or use the Services unless you agree
        to comply with all of the terms and conditions in this Agreement. You
        should therefore read this Agreement carefully and make sure you
        understand it. If you do not understand any of the terms and conditions
        of this Agreement, please contact us before using the Services by
        emailing us at{' '}
        <Link href='mailto:regulatory@plaid.com'>
          <a>regulatory@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        You may view an up-to-date copy of this Agreement at any time at{' '}
        <Link href='https://plaid.com/legal/#end-user-services-agreement-eea'>
          <a>https://plaid.com/legal/#end-user-services-agreement-eea</a>
        </Link>
        .
      </p>
      <p>
        <em>Jump to section:</em>
      </p>
      <p>
        <Link href='#plaid-eea'>
          <a data-smooth-scroll data-offset='80'>
            Plaid
          </a>
        </Link>
      </p>
      <p>
        <Link href='#plaid-services-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Plaid Services
          </a>
        </Link>
      </p>
      <p>
        <Link href='#eligibility-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Eligibility and Availability
          </a>
        </Link>
      </p>
      <p>
        <Link href='#communication-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Communication
          </a>
        </Link>
      </p>
      <p>
        <Link href='#data-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Data
          </a>
        </Link>
      </p>
      <p>
        <Link href='#liability-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Liability
          </a>
        </Link>
      </p>
      <p>
        <Link href='#complaints-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Complaints
          </a>
        </Link>
      </p>
      <p>
        <Link href='#changes-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Changes to this Agreement
          </a>
        </Link>
      </p>
      <p>
        <Link href='#termination-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Termination
          </a>
        </Link>
      </p>
      <p>
        <Link href='#law-end-user-eea'>
          <a data-smooth-scroll data-offset='80'>
            Governing law and language
          </a>
        </Link>
      </p>

      <p id='plaid-eea'>
        <strong>1. Plaid</strong>
      </p>
      <p>
        1.1. Plaid, BV (RSIN 860002652) is licensed by De Nederlandsche Bank
        N.V. for the provision of account information services (AIS) and
        registered with De Nederlandsche Bank N.V. under number R179714.
      </p>
      <p>
        1.2. Plaid's registered office address is Muiderstraat 1, 1011PZ
        Amsterdam, The Netherlands. Plaid's trading address is Suite 3A03,
        Singel 542, 1017AZ, Amsterdam, Nederland.
      </p>

      <p id='plaid-services-end-user-eea'>
        <strong>2. Plaid Services</strong>
      </p>
      <p>
        2.1 You have accessed our Services through an Application or within the
        Plaid dashboard (available at{' '}
        <Link href='https://my.plaid.com/'>
          <a>https://my.plaid.com/</a>
        </Link>
        ). The products and services provided to you by the Application are
        governed by a separate agreement between you and the provider of the
        Application ("
        <strong>Application Terms</strong>"). We have no responsibility for the
        products and services provided to you by or through the Application and
        will not be liable to you for any harm, damage or loss arising from your
        use of the products and services provided by or through the Application.
      </p>
      <p>
        2.2. Our Services will allow you to obtain financial information from
        your online bank or payment account ("<strong>Payment Account</strong>")
        (account information services).
      </p>
      <p>
        2.3. The terms and conditions that apply to the Payment Accounts that
        you access through our Services (the "<strong>Account Terms</strong>")
        will remain in effect and this Agreement does not change your Account
        Terms.
      </p>
      <p>
        2.4. We will not charge you for the use of our Services. Applications or
        other third parties may, however, charge you for products and services
        provided to you that make use of the Services provided by Plaid under
        this Agreement. Application providers and other third parties may pay us
        fees and other amounts in connection with the services we provide to
        them.
      </p>
      <p>
        <span style={{ textDecoration: 'underline' }}>
          Account information services
        </span>
      </p>
      <p>
        2.5. Plaid's account information services allow you to access and view
        information relating to your selected Payment Account within the Plaid
        dashboard (available at{' '}
        <Link href='https://my.plaid.com/'>
          <a>https://my.plaid.com/</a>
        </Link>
        ) and the Application through which you have accessed our Services.
      </p>
      <p>
        2.6. To access the Plaid dashboard, you need to create an account with
        Plaid. You also need to ensure that your information is accurate,
        complete and up-to-date. You must follow best practices to help secure
        your Plaid account and notify us if you learn of any unauthorised access
        to or use of your Plaid account.
      </p>
      <p>
        2.7. With your explicit consent, we access and display information
        relating to your selected Payment Account(s) ("
        <strong>Account Information</strong>") within the Plaid dashboard and
        transmit such information to the Application through which you have
        accessed our Services. Such information may include:
      </p>
      <p>
        (a) financial transaction history, for example, transaction amount,
        data, description and currency;
      </p>
      <p>
        (b) financial account details, for example, account number, type,
        currency and balance; and
      </p>
      <p>
        (c) financial account holder information, for example, name, address,
        phone number, and email address.
      </p>
      <p>
        2.8. Before receiving Plaid's account information service, you will have
        instructed the Application through which you have accessed our Services
        to retrieve your Account Information using Plaid. Upon such an
        instruction, you will generally be redirected to us.
      </p>
      <p>
        2.9. Once you are redirected to us, we will ask you to select which
        Payment Account provider ("<strong>Account Provider</strong>") you would
        like us to access Account Information from. You will give your explicit
        consent to us: (i) accessing your Account Information from the Account
        Provider you have selected; and (ii) taking the steps outlined in clause
        2.12 of this Agreement once we have accessed your Account Information.
        Plaid will request your Account Information from your Account Provider
        on a periodic basis in accordance with your consent until the consent
        expires or is withdrawn.
      </p>
      <p>
        2.10. Please note, you may be redirected to your Account Provider's
        website or mobile application in order to authenticate yourself so that
        your Account Provider knows that you consent to us accessing your
        Account Information.
      </p>
      <p>
        2.11. Alternatively, in some circumstances, we may ask you to provide us
        with the login details for your Payment Account. By providing us with
        your Payment Account login details, you confirm that you have all the
        necessary rights, permissions and authority to share your login details
        and Account Information with us, and you grant us explicit consent to
        use your login details to access your Payment Account to obtain the
        necessary Account Information on your behalf and at your direction in
        order to provide you with the Services.
      </p>
      <p>2.12. Once we have accessed your Account Information we may:</p>
      <p>
        (a) share your Account Information with the Application through which
        you have accessed our Services;
      </p>
      <p>
        (b) share your Account Information with third parties as directed by the
        Application through which you have accessed our Services provided,
        however, that you have explicitly consent to such sharing; or
      </p>
      <p>(c) display your Account Information on the Plaid dashboard.</p>
      <p>
        2.13. You agree to us sharing the Account Information we have accessed
        from your Account Provider with the Application through which you have
        accessed our Services, and with third parties as directed by that
        Application provided that in both cases you have explicitly consented to
        such sharing. Such sharing allows the Application to provide you with
        products and services in accordance with the Application Terms.
      </p>
      <p>
        2.14. We do not check the accuracy of the Account Information retrieved
        from your Account Provider and we rely on your Account Provider to
        ensure that your Account Information is up to date and accurate.
      </p>
      <p>
        2.15. We may standardize, categorize, merge, aggregate, and otherwise
        process your Account Information before displaying your Account
        Information on the Plaid dashboard, sharing it with the Application
        through which you have accessed our Services, or sharing it with third
        parties as directed by that Application with your explicit consent.
      </p>
      <p>
        2.16. You may withdraw or vary your consent for Plaid to provide account
        information services at any time.
      </p>

      <p id='eligibility-end-user-eea'>
        <strong>3. Eligibility and Availability</strong>
      </p>
      <p>3.1. You can only use the Services if:</p>
      <p>(a) you are 18 or over;</p>
      <p>
        (b) your Payment Account and Account Provider are located in the
        Netherlands or any other country in the EEA;
      </p>
      <p>
        (c) you provide us with accurate, complete, and up-to-date information,
        and do not misrepresent your identity or any other information about
        you;
      </p>
      <p>
        (d) you agree to this Agreement, and to using our Services in accordance
        with this Agreement; and
      </p>
      <p>
        (e) you agree to comply with all laws and regulations applicable to your
        use of the Services.
      </p>
      <p>
        3.2. The Services that are available and the manner in which the
        Services are delivered may vary depending on the type of device you are
        using, the type of Payment Account(s) you have, the Account Terms and
        the Application Terms.
      </p>

      <p id='communication-end-user-eea'>
        <strong>4. Communication</strong>
      </p>
      <p>
        4.1. Where relevant, Plaid may send you information and notifications to
        your email address or mobile number via SMS where Plaid considers this
        appropriate.
      </p>
      <p>
        4.2. If your contact details (including your mobile phone number or
        email address) change, you should tell us as soon as you can. You are
        responsible for maintaining and regularly checking your device or email
        inbox for information and notifications from Plaid.
      </p>
      <p>
        4.3. You can contact Plaid by emailing us at{' '}
        <Link href='mailto:regulatory@plaid.com'>
          <a>regulatory@plaid.com</a>
        </Link>
        .
      </p>

      <p id='data-end-user-eea'>
        <strong>5. Data</strong>
      </p>
      <p>
        5.1. We use your information in line with our End User Privacy Policy ("
        <strong>Privacy Policy</strong>") which can be found on our website at
        <Link href='https://plaid.com/legal/#end-user-privacy-policy'>
          <a>https://plaid.com/legal/#end-user-privacy-policy</a>
        </Link>
        . If you are not comfortable with how we handle your information as
        explained in the Privacy Policy, you should not use our Service.
      </p>

      <p id='liability-end-user-eea'>
        <strong>6. Liability</strong>
      </p>
      <p>
        6.1. Plaid will not be liable to refund you for any losses caused by
        circumstances beyond our control, for example, due to extreme weather,
        terrorist activity or industrial action.
      </p>
      <p>
        6.2. Plaid is not liable for any contravention of a requirement imposed
        on it by or under any applicable Dutch financial laws and regulation,
        where the contravention is due to:
      </p>
      <p>
        (a) abnormal or unforeseeable circumstances beyond Plaid's control, the
        consequences of which would have been unavoidable despite all efforts to
        the contrary; or
      </p>
      <p>
        (b) the obligations of Plaid under other provisions of EU or national
        law.
      </p>
      <p>6.3. Nothing excludes or limits our liability for:</p>
      <p>(a) death or personal injury caused by our negligence;</p>
      <p>(b) our fraud or fraudulent misrepresentation; or</p>
      <p>
        (c) a deliberate breach of this Agreement in a major way that is
        designed to harm you.
      </p>
      <p>
        6.4. We are not liable to you for any harm, damage or loss to you
        arising from the acts or omissions of any third parties, in particular
        your Account Provider(s) and the Application through which you have
        accessed our Services.
      </p>

      <p id='complaints-end-user-eea'>
        <strong>7. Complaints</strong>
      </p>
      <p>
        7.1. If you have a complaint about our Services, please email us at
        <Link href='mailto:regulatory@plaid.com'>
          <a>regulatory@plaid.com</a>
        </Link>{' '}
        so that Plaid can investigate the circumstances for you. We will aim to
        deal quickly and fairly with any complaints you have about our Services
        in accordance with our obligations under applicable law. Plaid may,
        however, direct you to:
      </p>
      <p>
        (a) your Account Provider, if your complaint relates to the services
        provided under the Account Terms; or
      </p>
      <p>
        (b) the Application through which you accessed our Services, if your
        complaint relates to the products and/or services provided by the
        Application under the Application Terms.
      </p>
      <p>
        7.2. If your complaint relates to our Services and we do not resolve it,
        you may be able to refer it to the Financial Services Complaints Board (
        <em>Klachteninstituut financiële dienstverlening – KiFiD</em>), within
        three months of receiving our final response regarding the complaint, or
        within one year after you have submitted your complaint with us. You can
        contact the Financial Services Complaints Board by telephone on: +3170
        333 89 99 on Monday to Friday, 9am to 7pm and on Saturday 9am to 1pm; by
        post at KiFiD, Postbus 93257, 2509 AG Den Haag, The Netherlands; or by
        email:{' '}
        <Link href='mailto:consumenten@kifid.nl'>
          <a>consumenten@kifid.nl</a>
        </Link>
        . Plaid is registered with the Financial Services Complaints Board under
        number R179714. You also have the right to directly bring your complaint
        before the competent civil courts of the Netherlands.
      </p>
      <p>
        7.3. Plaid is also not responsible for any complaints or disputes about
        products and/or services provided by the Application through which you
        accessed our Services, other third parties, or your Account Provider.
        You should settle these with the Application, third party, or Account
        Provider directly.
      </p>

      <p id='changes-end-user-eea'>
        <strong>8. Changes to this Agreement</strong>
      </p>
      <p>
        8.1. Plaid reserves the right to change this Agreement at any time.
        Plaid will take into account your interests affected by the intended
        changes and will only make changes that we consider to be reasonable.
      </p>
      <p>
        8.2. We will give you at least two months' prior written notice via, at
        a minimum, the contact details you have supplied to us of any intended
        material change to this Agreement along with the new version of the
        Agreement.
      </p>
      <p>
        8.3. If you do not agree with the proposed change(s) you must tell us
        using the Plaid contact details set out in clause 4.3 before that change
        takes effect and you will have the right to terminate this Agreement at
        any time before the proposed date of their entry into force. If you do
        not contact us in order to tell us that you do not accept the changes
        and request to terminate this Agreement you will be deemed to have
        accepted the change(s) to the Agreement.
      </p>

      <p id='termination-end-user-eea'>
        <strong>9. Termination</strong>
      </p>
      <p>
        9.1. The contract between you and Plaid which is set out in this
        Agreement and which governs your use of the Services will continue until
        cancelled in accordance with this clause 9.
      </p>
      <p>
        9.2. You have the right to cancel the contract between us, which is set
        out in this Agreement, at any time without notice by contacting Plaid
        using the Plaid contact details set out at clause 4.3.
      </p>
      <p>
        9.3. We may cancel the contract with you, with immediate effect, by
        giving written notice:
      </p>
      <p>
        (a) if you repeatedly break this Agreement and fail to resolve the
        matter to Plaid's satisfaction in a timely manner; or
      </p>
      <p>(b) in the event of your death or incapacity.</p>
      <p>
        9.4. We may cancel this Agreement with you for any reason by giving you
        at least 2 months' written notice.
      </p>

      <p id='law-end-user-eea'>
        <strong>10. Governing law and language</strong>
      </p>
      <p>
        10.1. This Agreement and any dispute or claim (including non-contractual
        disputes or claims) arising out of or in connection with it or its
        subject matter or formation shall be exclusively governed by and
        construed in accordance with the laws of the Netherlands.
      </p>
      <p>
        10.2. This Agreement is available in the English and Dutch language. All
        communications with you will be in English.
      </p>

      <hr className='section-divider' />
    </>
  );
};

export default EEAEndUserServicesAgreement;
